<script setup lang="ts">
import { useDialog } from '@/ui-plus/dialog/use-dialog';
import UpdatePasswordForm from '@auth-frontend-vue/components/forms/UpdatePasswordForm.vue';
import UpdateNameForm from '@auth-frontend-vue/components/forms/UpdateNameForm.vue';
import ChangeEmailForm from '@auth-frontend-vue/components/forms/ChangeEmailForm.vue';
import ChangePhoneForm from '@auth-frontend-vue/components/forms/ChangePhoneForm.vue';
import UserPasskeysList from "@auth-frontend-vue/components/UserPasskeysList.vue";
import {
    Dialog,
    DialogTrigger,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Fingerprint, KeyRound,
    Pencil,
    Lock,
    Mail,
    Phone,
} from "lucide-vue-next";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { useTimestamp } from "@vueuse/core";
import { authState } from "@/ui-plus/useAuth";
import { isFreshSessionLoggedInAt } from "@lib/consts/authConsts";
import { toast } from "@/ui-plus/sonner";
import { isPlaceholderEmail } from '@lib/utils/phoneToPlaceholderEmail';
import { maybePromptClaimEmail } from '@auth-frontend-vue/utils/claimEmailValidation';

const { t } = useI18n();

const dialog = useDialog();
const now = useTimestamp({ interval: 30_000 });
const isFreshSession = computed(() => {
    console.log('isFreshSession🔴')
    return isFreshSessionLoggedInAt(authState.value.accessTokenPayload?.loggedInAt, now.value)
});

function requireFreshSession(): boolean {
    if (isFreshSession.value) return true;
    toast.error(t('freshSessionRequired'), {
        description: t('pleaseLogoutAndLoginAgain'),
    });
    return false;
}

function openUpdatePasswordDialog() {
    if (!requireFreshSession()) return;
    dialog.show({
        title: t('updatePassword'),
        closeable: false,
        component: UpdatePasswordForm,
    });
}

function openUpdateNameDialog() {
    if (!requireFreshSession()) return;
    dialog.show({
        title: t('updateName'),
        closeable: false,
        component: UpdateNameForm,
    });
}

function openChangeEmailDialog() {
    if (!requireFreshSession()) return;
    dialog.show({
        title: t('changeEmail'),
        closeable: false,
        component: ChangeEmailForm,
    });
}

function openChangePhoneDialog() {
    if (!requireFreshSession()) return;
    // server enforces this too; pre-check for friendlier UX
    if (!authState.value.accessTokenPayload?.isEmailVerified) {
        toast.error(t('verifyEmailBeforePhoneChange'));
        return;
    }
    dialog.show({
        title: t('changePhone'),
        closeable: false,
        component: ChangePhoneForm,
    });
}


</script>

<template>
    <div>
        <div class="my-6 flex flex-col items-center gap-4 border border-border py-2">

            <p v-if="!isFreshSession"
               class="text-center text-xs text-muted-foreground">
                <Lock class="h-4 w-4 mr-1 inline-block" />
                {{ t('freshSessionRequired') }}.
                <br />
                {{ t('pleaseLogoutAndLoginAgain') }}.
            </p>

            <Dialog>
                <DialogTrigger as-child>
                    <Button variant="outline"
                            size="lg"
                            :disabled="!isFreshSession"
                            class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                        <Fingerprint class="h-5 w-5 mr-2 text-primary" />
                        Passkeys
                    </Button>
                </DialogTrigger>
                <DialogScrollContent>
                    <DialogHeader>
                        <DialogTitle>Add / Manage Passkeys</DialogTitle>
                        <DialogDescription />
                    </DialogHeader>
                    <UserPasskeysList :allow-mutations="isFreshSession" />
                </DialogScrollContent>
            </Dialog>

            <Button @click="openUpdatePasswordDialog"
                    variant="outline"
                    size="lg"
                    :disabled="!isFreshSession"
                    class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                <KeyRound class="h-4 w-4 mr-1" />
                {{ t('updatePassword') }}
            </Button>
            <Button @click="openUpdateNameDialog"
                    variant="outline"
                    size="lg"
                    :disabled="!isFreshSession"
                    class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                <Pencil class="h-4 w-4 mr-1" />
                {{ t('updateName') }}
            </Button>
            <Button v-if="authState.accessTokenPayload?.email && isPlaceholderEmail(authState.accessTokenPayload.email)"
                    @click="maybePromptClaimEmail"
                    variant="outline"
                    size="lg"
                    class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                <Mail class="h-4 w-4 mr-1" />
                {{ t('addEmail') }}
            </Button>
            <Button v-else
                    @click="openChangeEmailDialog"
                    variant="outline"
                    size="lg"
                    :disabled="!isFreshSession"
                    class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                <Mail class="h-4 w-4 mr-1" />
                {{ t('changeEmail') }}
            </Button>
            <Button v-if="authState.accessTokenPayload?.hasVerifiedPhoneNumber"
                    @click="openChangePhoneDialog"
                    variant="outline"
                    size="lg"
                    :disabled="!isFreshSession"
                    class="w-60 flex items-center justify-center shadow-sm border-2 border-primary/30 hover:border-primary bg-muted/50 hover:bg-accent transition-colors font-semibold text-base">
                <Phone class="h-4 w-4 mr-1" />
                {{ t('changePhone') }}
            </Button>
        </div>
    </div>
</template>
